import { CHAMPS_DU_PROFIL, type ChampDuProfil, type FeuilleDeProfil } from '@nextilt/commun';
import type { ChampsDuProfil, ChangementDeLigneDeProfil, ContactDuProfil, LigneDeProfil, ReponseProfil } from '../partage/api/types';

/**
 * Les règles pures de la page Profil (chantier profil, ticket 02, maquette du carnet
 * `profil.html`), testées dans `tests/client/profil/reglesDuProfil.test.ts`. Rien ne calcule avec
 * le profil : ces règles ne disent que ce que l'écran montre et ce qu'un geste envoie.
 */

/** Les feuilles de la page : les trois du profil, et les trois qui ne s'écrivent pas ici. */
export type FeuilleDeLaPage = FeuilleDeProfil | 'sensible' | 'propose' | 'contacts';

export type OngletDuProfil = 'tout' | 'moi' | 'moyens' | 'rythme' | 'contacts' | 'ia' | 'sensible';

/**
 * Les onglets de la boîte à fiches, dans l'ordre de la maquette. Sensible (fermé, `.clos`) ne
 * s'ouvre pas : sa feuille se lit dans Tout. Proposé par l'IA s'ouvre (chantier
 * profil-pour-claude, ticket 02).
 */
export const ONGLETS_DU_PROFIL: readonly { cle: OngletDuProfil; ouvrable: boolean }[] = [
  { cle: 'tout', ouvrable: true },
  { cle: 'moi', ouvrable: true },
  { cle: 'moyens', ouvrable: true },
  { cle: 'rythme', ouvrable: true },
  { cle: 'contacts', ouvrable: true },
  { cle: 'ia', ouvrable: true },
  { cle: 'sensible', ouvrable: false },
];

/** Les feuilles d'un onglet, en colonnes. Tout : les six, comme la maquette ; les autres : la leur. */
export function colonnesDeLOnglet(onglet: OngletDuProfil): FeuilleDeLaPage[][] {
  switch (onglet) {
    case 'tout':
      return [
        ['qui-je-suis', 'ce-que-j-ai'],
        ['ma-facon-de-faire', 'sensible'],
        ['propose', 'contacts'],
      ];
    case 'moi':
      return [['qui-je-suis']];
    case 'moyens':
      return [['ce-que-j-ai']];
    case 'rythme':
      return [['ma-facon-de-faire']];
    case 'contacts':
      return [['contacts']];
    case 'ia':
      return [['propose']];
    case 'sensible':
      return [['sensible']];
  }
}

/** Les champs de chaque feuille du profil (décision 2 d'Igor), dans l'ordre de `CHAMPS_DU_PROFIL`. */
export const CHAMPS_DE_LA_FEUILLE: Record<FeuilleDeProfil, readonly ChampDuProfil[]> = {
  'qui-je-suis': ['prenom', 'ville', 'quartier', 'situation', 'enRouteVers', 'lieuDeTravail', 'heuresDeTravail'],
  'ce-que-j-ai': ['permis', 'voiture', 'deplacement'],
  'ma-facon-de-faire': ['rythme', 'courage', 'meilleurMoment'],
};

/** Ce qui s'écrit dans un champ : rogné, et vide, rien (null). */
function ecritRogne(ecrit: string): string | null {
  const rogne = ecrit.trim();
  return rogne === '' ? null : rogne;
}

/**
 * Le profil entier après la correction d'un champ : `PUT /api/profil` remplace le profil, donc
 * tous les champs partent à chaque correction. `null` quand rien n'a changé : rien ne part.
 */
export function profilCorrige(profil: ChampsDuProfil, champ: ChampDuProfil, ecrit: string): ChampsDuProfil | null {
  const valeur = ecritRogne(ecrit);
  if (valeur === profil[champ]) return null;
  return { ...profil, [champ]: valeur };
}

/** Les champs encore vides, dans l'ordre des feuilles : tant qu'il y en a, « Compléter mon profil ». */
export function champsVides(profil: ChampsDuProfil): ChampDuProfil[] {
  return CHAMPS_DU_PROFIL.filter((champ) => profil[champ] === null);
}

/** Les cases du premier formulaire (ticket 03) à leur ouverture : ce qui est écrit, sinon vide. */
export function casesDuFormulaire(profil: ChampsDuProfil): Record<ChampDuProfil, string> {
  return Object.fromEntries(CHAMPS_DU_PROFIL.map((champ) => [champ, profil[champ] ?? ''])) as Record<ChampDuProfil, string>;
}

/**
 * Le profil entier qu'« Enregistrer » envoie (`PUT /api/profil` le remplace) : chaque case rognée,
 * vide, null. `null` quand rien n'a changé : rien ne part, seul le formulaire est noté vu.
 */
export function profilDuFormulaire(avant: ChampsDuProfil, cases: Record<ChampDuProfil, string>): ChampsDuProfil | null {
  const apres = Object.fromEntries(CHAMPS_DU_PROFIL.map((champ) => [champ, ecritRogne(cases[champ])])) as ChampsDuProfil;
  return CHAMPS_DU_PROFIL.some((champ) => apres[champ] !== avant[champ]) ? apres : null;
}

/**
 * Le formulaire s'ouvre seul (décisions 5 et 7 d'Igor) : à l'ordinateur, tant que le serveur dit
 * qu'il n'a pas été vu. Au téléphone, jamais ; le profil pas encore lu, pas encore.
 */
export function formulaireSOuvreSeul(auPouce: boolean, formulaireAMontrer: boolean | undefined): boolean {
  return !auPouce && formulaireAMontrer === true;
}

/** Le prénom rempli, qui remplace « Toi » sur l'accueil et l'e-mail dans la barre ; vide, `sinon`. */
export function nomDuCompte(prenom: string | null, sinon: string): string {
  return prenom === null ? sinon : (ecritRogne(prenom) ?? sinon);
}

/** Une ligne libre neuve, rognée ; rien d'écrit ni dans l'intitulé ni dans la valeur : rien ne part. */
export function ligneEcrite(intitule: string, valeur: string): { intitule: string; valeur: string } | null {
  const i = intitule.trim();
  const v = valeur.trim();
  if (i === '' && v === '') return null;
  return { intitule: i, valeur: v };
}

/** La correction d'une partie d'une ligne libre : seule elle part, rognée ; inchangée, rien. */
export function changementDeLigne(
  ligne: LigneDeProfil,
  partie: 'intitule' | 'valeur',
  ecrit: string
): ChangementDeLigneDeProfil | null {
  const rogne = ecrit.trim();
  if (rogne === ligne[partie]) return null;
  return { [partie]: rogne };
}

/** Le nom d'un contact ; sans nom, le titre de la bulle qui le porte. */
export function nomDuContact(contact: ContactDuProfil): string {
  return contact.nom ?? contact.bulle.titre;
}

/**
 * La lecture après Garder (chantier profil-pour-claude, ticket 02) : la proposition quitte les
 * propositions et entre à la fin de sa feuille, telle qu'on la donne (plus proposée). Donnée deux
 * fois, d'abord par l'écran puis par la réponse du serveur, elle ne fait qu'une ligne, la dernière.
 */
export function avecLaPropositionGardee(lu: ReponseProfil, gardee: LigneDeProfil): ReponseProfil {
  const feuille = lu.lignes[gardee.feuille];
  const deja = feuille.some((l) => l.id === gardee.id);
  return {
    ...lu,
    propositions: lu.propositions.filter((p) => p.id !== gardee.id),
    lignes: {
      ...lu.lignes,
      [gardee.feuille]: deja ? feuille.map((l) => (l.id === gardee.id ? gardee : l)) : [...feuille, gardee],
    },
  };
}

/** La lecture après Non : la proposition quitte les propositions, rien d'autre ne change. */
export function avecLaPropositionRetiree(lu: ReponseProfil, id: string): ReponseProfil {
  return { ...lu, propositions: lu.propositions.filter((p) => p.id !== id) };
}

/**
 * Les intercalaires du Profil au téléphone, dans l'ordre de la maquette (`SECTIONS_PROFIL`,
 * chantier telephone-profil, 01). Couché (02), le premier à gauche, les autres à droite.
 */
export const INTERCALAIRES_DU_PROFIL: readonly FeuilleDeLaPage[] = [
  'qui-je-suis',
  'ce-que-j-ai',
  'ma-facon-de-faire',
  'contacts',
  'propose',
  'sensible',
];

/**
 * Le compte d'une feuille qui s'écrit, à droite de son intercalaire : ses champs et ses lignes
 * libres remplis (une ligne libre l'est quand sa valeur est écrite), sur leur nombre.
 */
export function compteDeLaFeuille(lu: Pick<ReponseProfil, 'profil' | 'lignes'>, feuille: FeuilleDeProfil): { remplis: number; total: number } {
  const champs = CHAMPS_DE_LA_FEUILLE[feuille];
  const lignes = lu.lignes[feuille];
  const remplis = champs.filter((c) => lu.profil[c] !== null).length + lignes.filter((l) => l.valeur !== '').length;
  return { remplis, total: champs.length + lignes.length };
}

/**
 * Un champ en curseur (le permis, la voiture, l'énergie) : sa valeur quand elle n'est pas l'un des
 * crans (écrite à l'ordinateur), pour la montrer telle quelle sous le curseur ; vide ou l'un des
 * crans, rien.
 */
export function valeurHorsDesCrans(valeur: string | null, crans: readonly string[]): string | null {
  return valeur === null || crans.includes(valeur) ? null : valeur;
}

/** L'initiale du prénom, dans le rond du haut de la page ; sans prénom, rien. */
export function initialeDuProfil(prenom: string | null): string {
  const premiere = prenom?.trim()[0];
  return premiere ? premiere.toLocaleUpperCase('fr') : '';
}
